import React, { useState, useEffect } from "react";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../ui/select";
import { BaseFormFields, useBaseForm } from "./BaseFormFields";
import { useApp } from "../../context/AppContext";
import * as api from "../../lib/api";

const EMPLOYMENT_TYPES = [
    { id: "cdi", label: "CDI" },
    { id: "cdd", label: "CDD" },
    { id: "intern", label: "Stage" },
    { id: "external", label: "Externe" },
    { id: "freelance", label: "Freelance" },
];

export function OnboardingForm({ onSubmit, initialData, readOnly, submitting, onTitleChange, allowedPriorities, ticketType, onTypeChange }) {
    const baseForm = useBaseForm("high");
    const { departments, users } = useApp();
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [arrivalDate, setArrivalDate] = useState("");
    const [jobTitle, setJobTitle] = useState("");
    const [departmentId, setDepartmentId] = useState("");
    const [managerId, setManagerId] = useState("");
    const [employmentType, setEmploymentType] = useState("cdi");
    const [templateId, setTemplateId] = useState("");
    const [equipment, setEquipment] = useState([]);
    const [selectedEquipment, setSelectedEquipment] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [equipmentCatalog, setEquipmentCatalog] = useState([]);
    const [notes, setNotes] = useState("");

    useEffect(() => {
        loadCatalogs();
    }, []);

    const loadCatalogs = async () => {
        try {
            const [templatesData, equipmentData] = await Promise.all([
                api.getWorkstationTemplates(),
                api.getEquipmentCatalog(),
            ]);
            setTemplates(templatesData);
            setEquipmentCatalog(equipmentData);
        } catch (error) {
            console.error("Failed to load catalogs:", error);
        }
    };

    const toggleEquipment = (eqId) => {
        setSelectedEquipment((prev) =>
            prev.includes(eqId) ? prev.filter((id) => id !== eqId) : [...prev, eqId]
        );
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        onSubmit({
            title: baseForm.title || `Arrivée - ${firstName} ${lastName}`,
            description: baseForm.description,
            priority: baseForm.priority,
            dynamic_fields: {
                employee_first_name: firstName,
                employee_last_name: lastName,
                arrival_date: arrivalDate,
                job_title: jobTitle,
                department_id: departmentId,
                manager_id: managerId,
                employment_type: employmentType,
                workstation_template_id: templateId,
                equipment_needed: selectedEquipment,
                notes: notes,
            },
        });
    };

    return (
        <form onSubmit={handleSubmit}>
            <BaseFormFields
                ticketType={ticketType}
                onTypeChange={onTypeChange}
                {...baseForm}
                onTitleChange={onTitleChange}
                    allowedPriorities={allowedPriorities}
                titlePlaceholder="Ex: Arrivée - Emma Durand (Marketing)"
                descriptionPlaceholder="Informations complémentaires..."
            >
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg mb-4">
                    <h3 className="text-sm font-semibold text-blue-900 mb-3">
                        Informations du collaborateur
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <Label className="form-label">Prénom</Label>
                            <Input
                                value={firstName}
                                onChange={(e) => setFirstName(e.target.value)}
                                data-testid="first-name"
                            />
                        </div>
                        <div>
                            <Label className="form-label">Nom</Label>
                            <Input
                                value={lastName}
                                onChange={(e) => setLastName(e.target.value)}
                                data-testid="last-name"
                            />
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="form-group">
                        <Label className="form-label">Date d'arrivée</Label>
                        <Input
                            type="date"
                            value={arrivalDate}
                            onChange={(e) => setArrivalDate(e.target.value)}
                            data-testid="arrival-date"
                        />
                    </div>

                    <div className="form-group">
                        <Label className="form-label">Intitulé du poste</Label>
                        <Input
                            value={jobTitle}
                            onChange={(e) => setJobTitle(e.target.value)}
                            placeholder="Ex: Chargé de communication"
                            data-testid="job-title"
                        />
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="form-group">
                        <Label className="form-label">Département</Label>
                        <Select value={departmentId} onValueChange={setDepartmentId}>
                            <SelectTrigger data-testid="department">
                                <SelectValue placeholder="Sélectionner" />
                            </SelectTrigger>
                            <SelectContent>
                                {departments.map((d) => (
                                    <SelectItem key={d.id} value={d.id}>
                                        {d.name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="form-group">
                        <Label className="form-label">Manager</Label>
                        <Select value={managerId} onValueChange={setManagerId}>
                            <SelectTrigger data-testid="manager">
                                <SelectValue placeholder="Sélectionner" />
                            </SelectTrigger>
                            <SelectContent>
                                {users.map((u) => (
                                    <SelectItem key={u.id} value={u.id}>
                                        {u.first_name} {u.last_name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="form-group">
                        <Label className="form-label">Type de contrat</Label>
                        <Select value={employmentType} onValueChange={setEmploymentType}>
                            <SelectTrigger data-testid="employment-type">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {EMPLOYMENT_TYPES.map((t) => (
                                    <SelectItem key={t.id} value={t.id}>
                                        {t.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="form-group">
                        <Label className="form-label">Modèle de poste</Label>
                        <Select value={templateId} onValueChange={setTemplateId}>
                            <SelectTrigger data-testid="workstation-template">
                                <SelectValue placeholder="Sélectionner" />
                            </SelectTrigger>
                            <SelectContent>
                                {templates.map((t) => (
                                    <SelectItem key={t.id} value={t.id}>
                                        {t.name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                <div className="form-group">
                    <Label className="form-label">Matériel nécessaire</Label>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2 border rounded-md p-3">
                        {equipmentCatalog.map((eq) => (
                            <label
                                key={eq.id}
                                className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer"
                            >
                                <Checkbox
                                    checked={selectedEquipment.includes(eq.id)}
                                    onCheckedChange={() => toggleEquipment(eq.id)}
                                    data-testid={`equipment-${eq.id}`}
                                />
                                <span className="text-sm">{eq.name}</span>
                            </label>
                        ))}
                    </div>
                </div>

                <div className="form-group">
                    <Label className="form-label">Notes</Label>
                    <Textarea
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder="Informations complémentaires (bureau, besoins spécifiques...)"
                        data-testid="notes"
                    />
                </div>
            </BaseFormFields>

            <div className="mt-6 flex justify-end">
                <Button
                    type="submit"
                    disabled={!baseForm.title || submitting}
                    data-testid="submit-ticket"
                >
                    {submitting ? "Création..." : "Soumettre le ticket"}
                </Button>
            </div>
        </form>
    );
}
